import { IMO } from '../models/voyage-imo-model';
import { authFetchGET, authFetchPOST, authFetchPUT } from '../utils/auth-fetch';
import { VesselPositionData, VesselTrackData } from './types/position-service.types';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const positionsApi = (<any>window)["appenv"].services.positionsapi;
const env = { ...positionsApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };


const endpointMapping: Record<string, string> = {
    "positions/manualoverride": "vessel/manualoverride",
    "positions/manual": "vessel/manual",
    "positions": "vessel/positions",
    "positions/latest": "vessel/latest",
};

function getApiEndpoint(endpoint: string, imoNo?: IMO): string {
    if (isVesselOnCloud && endpointMapping[endpoint]) {
        return imoNo ? `${endpointMapping[endpoint]}/${imoNo}` : endpointMapping[endpoint]; 
    }
    return endpoint; 
}
export class PositionsService {

    // Gets the most recent position of multiple vessels.
    static async currentPosition_VesselVoyage(imoNos: number[]): Promise<VesselPositionData[]> {
        const endpointUrl = getApiEndpoint("positions/latest");
        // Size of URL is limited to 8000 bytes, so fetch 100 vessels at a time, more than that and API response is inconsistent
        const imoBatches = [];
        for (let i = 0; i < imoNos.length; i += 100) {
            imoBatches.push(imoNos.slice(i, i + 100));
        }

        const promises = imoBatches.map(imoBatch => {
            const imoQuery = imoBatch.map(imo => "imo=" + imo).join("&");
            return authFetchGET(env.scope, `${env.baseurl}/${endpointUrl}?${imoQuery}`);
        });

        const results = await Promise.all(promises);
        const data: any[] = (<any[]>[]).concat(...results);
        
        return data.filter(item => item?.imo !== undefined).map(item => {
            return {
                imo: item.imo,
                vesselName: item.vesselName,
                timestamp: item.timestamp,
                latitude: item.latitude,
                longitude: item.longitude,
                heading: item.heading,
                course: item.course,
                sog: item.sog,
                source: item.source,
                dataSources: item.dataSources,
                readingType: item.readingType,
                navStatus: item.navStatus,
                vesselWidth: item.vesselWidth,
                vesselLength: item.vesselHeight,
                etaScheduled: item.etaScheduled, 
                etaCalc: item.etaCalc, 
                currUnlocode: item.currUnlocode, 
                nextUnlocode: item.nextUnlocode,
                isManualNeeded: item.isManualNeeded,
            }
        });
        
    }


    static async getLatest(): Promise<VesselPositionData[] | null> {
        try {
            return await authFetchGET(env.scope, `${env.baseurl}/search?n=1000000`);
        } catch (err) {
            console.warn(`NO LATEST POSITIONS DATA`);
            return null;
        }
    }

    // Gets a time range of positions for a specific vessel.
    static async getVesselTrack_VesselVoyage(imoNo: number, fromDate: Date, toDate: Date): Promise<VesselTrackData|null>
    {
        const endpointUrl = getApiEndpoint("positions");

        const data: VesselTrackData = await authFetchGET(env.scope, `${env.baseurl}/${endpointUrl}/${imoNo}`,
            {from:fromDate.toISOString(),to:toDate.toISOString()});

        if (!data?.positions.length)
        {
            console.warn(`NO POSITIONS DATA for ${imoNo}`);
            return null;
        }

        return data;
    }

    // Get manual override state
    static async getManualPositionOverride(imoNo: number) {
        const endpointUrl = getApiEndpoint("positions/manualoverride", imoNo);

        const data: {manualPositionOverride: boolean} = await authFetchGET(env.scope, `${env.baseurl}/${endpointUrl}`, isVesselOnCloud && { imo: `${imoNo}` });

        return data.manualPositionOverride;
    }

    // Set manual override state
    static async setManualPositionOverride(override: boolean, imoNo?: IMO) {
        const endpointUrl = getApiEndpoint("positions/manualoverride", imoNo);
        const data = { manualPositionOverride: override };

        if(isVesselOnCloud){
            await authFetchPOST(env.scope, `${env.baseurl}/${endpointUrl}`, data);
        }
        else {
            await authFetchPUT(env.scope, `${env.baseurl}/${endpointUrl}`, data);
        }
    }

    // Add manual position
    static async setManualPosition(coordinates: [number, number], timestamp: string, imoNo?: IMO) {
        const endpointUrl = getApiEndpoint("positions/manual", imoNo);
        const data = {
            timestamp: timestamp,
            latitude: coordinates[1],
            longitude: coordinates[0]
        };
        await authFetchPOST(env.scope, `${env.baseurl}/${endpointUrl}`, data);
    }

    static async getCurrentTimeZone(imoNo: number): Promise<string|null> {
        try {
            const timezone: { timezone: string } = await authFetchGET(env.scope, `${env.baseurl}/positions/currenttimezone?imo=${imoNo}`);
            return timezone.timezone;
        } catch (err) {
            console.warn(`NO TIMEZONE DATA`);
            return null;
        }
    }
}
